import { action, computed, makeAutoObservable } from 'mobx';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import {
    FilterMenuItemType,
    FilterStateType,
} from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/filters.type';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CampaignStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Common } from 'src/AppState/Common';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { DateTime } from 'src_common/utils/time/time';
import { PaginationConfigType } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';

export const validStatuses: CampaignStatusType[] = ['draft', 'active', 'paused', 'ready', 'expired'];
type SortableKeysType = keyof CampaignModel | 'rewardName';

const pageSizeOptions: number[] = [10, 25, 50, 100];

interface SelectValueType {
    value: string;
    label: string;
    onClick: () => void;
}

export class CampaignListState implements FilterStateType {
    private static instance: CampaignListState | null = null;

    public selectedCampaignsSet = new Set<string>();

    public isAllExpanded: boolean = false;
    public isExpiredHidden: boolean = false;

    public selectedRewards: string[] = [];
    public selectedStatuses: string[] = [];
    public selectedPlayerType: string[] = [];
    public selectedOptIn: string[] = [];
    public selectedHasPromoCode: string[] = [];
    public selectedAction: string[] = [];

    public expandedRows: string[] = [];
    public filter: FormInputState<string, string> = FormInputState.new('');
    public sortConfig: { key: SortableKeysType; direction: 'ascending' | 'descending' } | null = null;
    public currentPage: number = 1;
    public rowsPerPage: number = 10;
    public rowsPerPageState = FormInputState.new('');

    public constructor(
        private common: Common,
        private readonly routerBackoffice: RouterBackoffice
    ) {
        const route = this.routerBackoffice.getPrevValueString('global');

        if (CampaignListState.instance !== null && route === 'bonus-engine') {
            return CampaignListState.instance;
        }

        makeAutoObservable(this);
        this.setRowsPerPage(this.rowsPerPage);

        CampaignListState.instance = this;
    }

    public get isNoFilterSelected(): boolean {
        return (
            this.selectedCampaignsSet.size === 0 &&
            this.selectedRewards.length === 0 &&
            this.selectedStatuses.length === 0 &&
            this.selectedOptIn.length === 0 &&
            this.selectedHasPromoCode.length === 0 &&
            this.selectedPlayerType.length === 0 &&
            this.selectedAction.length === 0
        );
    }

    @action public setFilter = (value: string): void => {
        this.filter.setValue(value);
        this.currentPage = 1;
    };

    @action public setSortConfig = (key: keyof CampaignModel | 'rewardName'): void => {
        if (this.sortConfig !== null && this.sortConfig.key === key) {
            this.sortConfig.direction = this.sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        } else {
            this.sortConfig = { key, direction: 'ascending' };
        }
    };

    @action public setPage = (page: number): void => {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    };

    @action public toggleRowExpansion = (campaignId: string): void => {
        if (this.expandedRows.includes(campaignId)) {
            this.expandedRows = this.expandedRows.filter((id) => id !== campaignId);
        } else {
            this.expandedRows.push(campaignId);
        }
    };

    @action public toggleHideExpired = (): void => {
        this.isExpiredHidden = !this.isExpiredHidden;
        this.setPage(1);
    };

    @action public expandAllWithDescription = (): void => {
        const campaignStore = CampaignStore.get(this.common);
        const campaigns = campaignStore.allCampaigns;

        const campaignsWithDescription = campaigns
            .filter((campaign) => campaign.description)
            .map((campaign) => campaign.id);

        if (this.isAllExpanded) {
            this.expandedRows = this.expandedRows.filter((id) => !campaignsWithDescription.includes(id));
            this.isAllExpanded = false;
        } else {
            this.expandedRows = Array.from(new Set([...this.expandedRows, ...campaignsWithDescription]));
            this.isAllExpanded = true;
        }
    };

    @action public setRowsPerPage = (rows: number): void => {
        this.rowsPerPage = rows;
        this.rowsPerPageState.setValue(rows.toString());
        this.currentPage = 1;
    };

    public get selectValues(): SelectValueType[] {
        return pageSizeOptions.map((size) => ({
            value: size.toString(),
            label: size.toString(),
            onClick: () => this.setRowsPerPage(size),
            dataTest: `page-size-${size}`,
        }));
    }

    private filterCampaigns(data: Array<CampaignModel>): Array<CampaignModel> {
        if (this.isExpiredHidden) {
            data = data.filter((campaign) => campaign.status !== 'expired');
        }

        const filterText = this.filter.value.toLowerCase();

        if (filterText !== '') {
            data = data.filter((campaign) => {
                const matchesName = campaign.campaignName.toLowerCase().includes(filterText);
                const matchesDescription = campaign.description.toLowerCase().includes(filterText);
                return matchesName || matchesDescription;
            });
        }

        if (this.selectedStatuses.length > 0) {
            data = data.filter((campaign) => this.selectedStatuses.includes(campaign.status));
        }

        if (this.selectedCampaignsSet.size > 0) {
            data = data.filter((campaign) => this.selectedCampaignsSet.has(campaign.campaignType));
        }

        if (this.selectedRewards.length > 0) {
            data = data.filter((campaign) =>
                campaign.bonusRewards?.some((reward) =>
                    reward.rewardType === undefined || reward.rewardType === null
                        ? false
                        : this.selectedRewards.includes(reward.rewardType)
                )
            );
        }

        if (this.selectedPlayerType.length > 0) {
            data = data.filter((campaign) =>
                campaign.playerType === undefined || campaign.playerType === null
                    ? false
                    : this.selectedPlayerType.includes(campaign.playerType)
            );
        }

        if (this.selectedOptIn.length > 0) {
            const optIn = this.selectedOptIn.includes('yes');
            data = data.filter((campaign) => (optIn ? campaign.optInOnly === true : campaign.optInOnly !== true));
        }

        if (this.selectedHasPromoCode.length > 0) {
            const hasPromoCode = this.selectedHasPromoCode.includes('yes');
            data = data.filter((campaign) => (campaign.promoCodesArray !== null) === hasPromoCode);
        }

        if (this.selectedAction.length > 0) {
            data = data.filter((campaign) =>
                campaign.actionConditions?.bettingPlatform === undefined ||
                campaign.actionConditions.bettingPlatform === null
                    ? false
                    : this.selectedAction.includes(campaign.actionConditions.bettingPlatform)
            );
        }
        return data;
    }

    private sortCampaigns(data: Array<CampaignModel>): Array<CampaignModel> {
        if (this.sortConfig === null) {
            return data;
        }

        const { key, direction } = this.sortConfig;

        return [...data].sort((a, b) => {
            const getValue = (
                campaign: CampaignModel,
                key: keyof CampaignModel | 'rewardName'
            ): string | number | undefined => {
                if (key === 'rewardName') {
                    return campaign.bonusRewards?.[0]?.rewardName ?? '';
                }

                const value = campaign[key];

                if (value instanceof DateTime) {
                    return value.unixMs();
                }

                return typeof value === 'string' || typeof value === 'number' ? value : undefined;
            };

            const aValue = getValue(a, key);
            const bValue = getValue(b, key);

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'ascending' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    }

    @computed public get filteredData(): Array<CampaignModel> {
        const campaignStore = CampaignStore.get(this.common);
        let data = campaignStore.allCampaigns;

        data = this.filterCampaigns(data);

        return this.sortCampaigns(data);
    }

    @computed public get paginatedData(): Array<CampaignModel> {
        const data = this.filteredData;

        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        return data.slice(startIndex, startIndex + this.rowsPerPage);
    }

    @computed public get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    }

    @computed public get totalItems(): number {
        return this.filteredData.length;
    }

    @computed public get startItem(): number {
        if (this.totalItems === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.rowsPerPage + 1;
    }

    @computed public get endItem(): number {
        return Math.min(this.startItem + this.rowsPerPage - 1, this.totalItems);
    }

    @computed public get paginationConfig(): PaginationConfigType<string> {
        return {
            currentPage: this.currentPage,
            endItem: this.endItem,
            setPage: this.setPage,
            startItem: this.startItem,
            totalItems: this.totalItems,
            totalPages: this.totalPages,
            rowsPerPageState: this.rowsPerPageState,
            selectValues: this.selectValues,
        };
    }

    /*************** Filters **************/
    @computed public get filterMenuItems(): FilterMenuItemType[] {
        return [
            {
                label: 'Campaign',
                items: [
                    { key: 'bet-and-get', label: 'Bet and Get' },
                    { key: 'settlement', label: 'Settlement' },
                    { key: 'promo-code', label: 'Promo Code' },
                ],
            },
            {
                label: 'Reward',
                items: [
                    { key: 'free-bet', label: 'Free Bet' },
                    { key: 'free-spin', label: 'Free Spin' },
                ],
            },
            {
                label: 'Status',
                items: [
                    { key: 'active', label: 'Active' },
                    { key: 'draft', label: 'Draft' },
                    { key: 'ready', label: 'Ready' },
                    { key: 'paused', label: 'Paused' },
                    { key: 'expired', label: 'Expired' },
                ],
            },
            {
                label: 'Player Type',
                items: [
                    { key: 'all-players', label: 'All players' },
                    { key: 'new-players', label: 'New Players' },
                    { key: 'segmented-customers', label: 'Segmented Customers' },
                ],
            },
            {
                label: 'Opt In',
                items: [
                    { key: 'yes', label: 'Yes' },
                    { key: 'no', label: 'No' },
                ],
            },
            {
                label: 'Has Promo Code',
                items: [
                    { key: 'yes', label: 'Yes' },
                    { key: 'no', label: 'No' },
                ],
            },
            {
                label: 'Action',
                items: [
                    { key: 'sports-book', label: 'Sports Book' },
                    { key: 'casino', label: 'Casino' },
                ],
            },
        ];
    }

    @action public toggleSelectedCampaign = (campaignKey: string): void => {
        if (this.selectedCampaignsSet.has(campaignKey)) {
            this.selectedCampaignsSet.delete(campaignKey);
        } else {
            this.selectedCampaignsSet.add(campaignKey);
        }
        this.setPage(1);
    };

    @action public toggleSelectedReward = (rewardKey: string): void => {
        if (this.selectedRewards.includes(rewardKey)) {
            this.selectedRewards = this.selectedRewards.filter((r) => r !== rewardKey);
        } else {
            this.selectedRewards.push(rewardKey);
        }
        this.setPage(1);
    };

    @action public toggleSelectedStatus = (statusKey: string): void => {
        if (this.selectedStatuses.includes(statusKey)) {
            this.selectedStatuses = this.selectedStatuses.filter((s) => s !== statusKey);
        } else {
            this.selectedStatuses.push(statusKey);
        }
        this.setPage(1);
    };

    @action public toggleSelectedPlayerType = (playerTypeKey: string): void => {
        if (this.selectedPlayerType.includes(playerTypeKey)) {
            this.selectedPlayerType = this.selectedPlayerType.filter((s) => s !== playerTypeKey);
        } else {
            this.selectedPlayerType.push(playerTypeKey);
        }
        this.setPage(1);
    };

    @action public toggleSelectedOptIn = (optInKey: string): void => {
        this.selectedOptIn = this.selectedOptIn.includes(optInKey) ? [] : [optInKey];
        this.setPage(1);
    };

    @action public toggleSelectedHasPromoCode = (hasPromoCodeKey: string): void => {
        this.selectedHasPromoCode = this.selectedHasPromoCode.includes(hasPromoCodeKey) ? [] : [hasPromoCodeKey];
        this.setPage(1);
    };

    @action public toggleSelectedAction = (actionKey: string): void => {
        if (this.selectedAction.includes(actionKey)) {
            this.selectedAction = this.selectedAction.filter((s) => s !== actionKey);
        } else {
            this.selectedAction.push(actionKey);
        }
        this.setPage(1);
    };

    @action public clearFilters = (): void => {
        this.selectedCampaignsSet.clear();
        this.selectedRewards = [];
        this.selectedStatuses = [];
        this.selectedPlayerType = [];
        this.selectedOptIn = [];
        this.selectedHasPromoCode = [];
        this.selectedAction = [];
        this.setPage(1);
    };
}
